// src/components/ReportFilters.tsx
import React, { useState } from 'react';
import { Filter, X, Calendar, MapPin, CheckCircle, AlertCircle } from 'lucide-react';

interface ReportFiltersProps {
  filters: any;
  onChange: (filters: any) => void;
}

const ReportFilters: React.FC<ReportFiltersProps> = ({ filters, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const categories = [
    { value: 'WATER_AND_SANITATION', label: 'Water & Sanitation' },
    { value: 'ROADS_AND_TRANSPORT', label: 'Roads & Transport' },
    { value: 'HEALTHCARE', label: 'Healthcare' },
    { value: 'EDUCATION', label: 'Education' },
    { value: 'SECURITY', label: 'Security' },
    { value: 'WASTE_MANAGEMENT', label: 'Waste Management' },
    { value: 'ELECTRICITY', label: 'Electricity' },
    { value: 'CORRUPTION', label: 'Corruption' }
  ];

  const statuses = [
    { value: 'PENDING', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'IN_PROGRESS', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
    { value: 'RESOLVED', label: 'Resolved', color: 'bg-green-100 text-green-800' }
  ];

  const wards = [
    { id: 1, name: 'Biashara Ward' },
    { id: 2, name: 'Kaptembwo Ward' },
    { id: 3, name: 'Kapkures Ward' },
    { id: 4, name: 'London Ward' },
    { id: 5, name: 'Naivasha East Ward' }
  ];

  const clearFilters = () => {
    onChange({
      status: '',
      category: '',
      wardId: '',
      startDate: '',
      endDate: ''
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="text-gray-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-900">Filter Reports</h3>
        </div>
        <div className="flex gap-2">
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Clear All
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            {isExpanded ? <X size={20} /> : <Filter size={20} />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <CheckCircle className="mr-2" size={16} />
                Status
              </label>
              <div className="space-y-2">
                {statuses.map((status) => (
                  <label key={status.value} className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      checked={filters.status === status.value}
                      onChange={() => onChange({ ...filters, status: status.value })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className={`ml-3 px-2 py-1 rounded-full text-xs ${status.color}`}>
                      {status.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <AlertCircle className="mr-2" size={16} />
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => onChange({ ...filters, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Ward Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <MapPin className="mr-2" size={16} />
                Ward
              </label>
              <select
                value={filters.wardId}
                onChange={(e) => onChange({ ...filters, wardId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Wards</option>
                {wards.map((ward) => (
                  <option key={ward.id} value={ward.id}>
                    {ward.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center">
                <Calendar className="mr-2" size={16} />
                Date Range
              </label>
              <div className="space-y-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => onChange({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => onChange({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Urgency Filter */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Minimum Urgency Score
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="10"
                value={filters.minUrgencyScore || 1}
                onChange={(e) => onChange({ ...filters, minUrgencyScore: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm font-medium text-gray-700">
                {filters.minUrgencyScore || 1}+
              </span>
            </div>
          </div>

          {/* Active Filters */}
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm font-medium text-gray-700 mb-2">Active Filters:</p>
            <div className="flex flex-wrap gap-2">
              {filters.status && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  Status: {statuses.find(s => s.value === filters.status)?.label}
                  <button
                    onClick={() => onChange({ ...filters, status: '' })}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.category && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-green-100 text-green-800">
                  Category: {categories.find(c => c.value === filters.category)?.label}
                  <button
                    onClick={() => onChange({ ...filters, category: '' })}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
              {filters.wardId && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                  Ward: {wards.find(w => w.id.toString() === filters.wardId)?.name}
                  <button
                    onClick={() => onChange({ ...filters, wardId: '' })}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
              {(filters.startDate || filters.endDate) && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                  Date: {filters.startDate || 'Start'} to {filters.endDate || 'End'}
                  <button
                    onClick={() => onChange({ ...filters, startDate: '', endDate: '' })}
                    className="ml-2 text-orange-600 hover:text-orange-800"
                  >
                    <X size={12} />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportFilters;